<script>
  import Button from '@leaflink/stash/Button.vue';
  import tooltip from '@leaflink/stash/directives/tooltip';
  import Icon from '@leaflink/stash/Icon.vue';
  import Input from '@leaflink/stash/Input.vue';
  import { storeToRefs } from 'pinia';
  import { mapActions } from 'vuex';

  import { DISCOUNT_TYPES } from '@/constants';
  import DiscountTag from '@/pages/Shop/_components/DiscountTag';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import { useDeleteCartItem } from '@/queries/cart/cart.query';
  import { trackBuyerProductEdited, trackBuyerProductRemoved } from '@/segment';
  import { money } from '@/utils/i18n';
  import { encodeSegmentTrackPageDataToQueryParams } from '@/utils/segmentTrackPageData';

  export default {
    name: 'miniCartItem',

    components: {
      Button,
      DiscountTag,
      Icon,
      Input,
    },

    directives: {
      tooltip,
    },

    props: {
      /**
       * The campaign label if a campaign exists
       */
      campaignLabel: {
        type: String,
        default: '',
      },
      company: {
        type: Object,
        default: () => ({}),
      },
      item: {
        type: Object,
        default: () => ({}),
      },
    },

    setup() {
      const { getIsSplitEnabled, user } = storeToRefs(useUserStore());

      const { mutateAsync: deleteCartItem } = useDeleteCartItem();

      return {
        isSplitEnabled: getIsSplitEnabled,
        deleteCartItem,
        algoliaQueryId: user.value.algoliaQueryId,
      };
    },

    data() {
      return {
        isDeleting: false,
      };
    },

    computed: {
      product() {
        return this.item.product;
      },
      displayedValidationErrors() {
        const { validationErrors, updateValidationErrors } = this.item;
        let errors = [];

        if (updateValidationErrors) {
          errors = Object.values(updateValidationErrors).flat();
        } else {
          errors = validationErrors;
        }

        return errors;
      },
      trackingData() {
        const { company, item } = this;
        const { address, licenses, name, state } = company;
        const licenseNumber = licenses[0]?.number || '';

        return {
          company: `${name} (${address}, ${state}) ${licenseNumber}`,
          product_id: item.product.id,
        };
      },

      hasSaleDiscount() {
        return this.item.discountType === DISCOUNT_TYPES.sale.id;
      },

      showVolumeDiscountIndicator() {
        return this.item.volumeDiscount;
      },
    },

    methods: {
      ...mapActions('cart', ['clearCartItemError', 'updateCartItem', 'fetchCartCount']),

      encodeSegmentTrackPageDataToQueryParams,
      money,

      /**
       * Updates the cart quantity
       * @param {number} quantity - the new item quantity
       * @returns {void}
       */
      updateQuantity(quantity) {
        const { item, trackingData } = this;

        this.updateCartItem({ ...item, quantity });

        trackBuyerProductEdited(trackingData);
      },

      /**
       * Remove item from the cart
       */
      async remove() {
        if (this.isDeleting) {
          return;
        }

        const { item, trackingData } = this;

        try {
          this.isDeleting = true;
          await this.deleteCartItem({ id: item.id });
          await this.fetchCartCount();
        } finally {
          this.isDeleting = false;
        }

        trackBuyerProductRemoved(trackingData);
      },

      /**
       * Determines if a given field is in error
       * @param {string} field - the field being checked
       * @returns {boolean}
       */
      hasError(field) {
        const { updateValidationErrors } = this.item;

        return updateValidationErrors && Object.keys(updateValidationErrors).indexOf(field) !== -1;
      },
    },
  };
</script>

<template>
  <li class="flex ll-py-3" :class="$style.cartItem">
    <router-link
      :to="{
        name: 'product-details',
        params: { company: company.slug, productId: product.id },
        query: {
          segment_page_event_data: encodeSegmentTrackPageDataToQueryParams({
            data: {
              algolia: {
                query_id: algoliaQueryId,
              },
            },
          }),
        },
      }"
    >
      <img class="rounded" :class="$style.image" :src="product.featuredImage" />
    </router-link>
    <div class="flex-1 ll-pl-2" :class="$style.details">
      <div :class="$style.detailsRow">
        <router-link
          class="flex-1"
          :class="$style.name"
          :to="{
            name: 'product-details',
            params: { company: company.slug, productId: product.id },
            query: {
              segment_page_event_data: encodeSegmentTrackPageDataToQueryParams({
                data: {
                  algolia: {
                    query_id: algoliaQueryId,
                  },
                },
              }),
            },
          }"
        >
          {{ product.displayName }}
        </router-link>
        <Button icon :class="$style.remove" :disabled="isDeleting" @click="remove">
          <Icon v-if="isDeleting" class="fx-spin" name="working" />
          <Icon v-else name="trashcan" />
        </Button>
      </div>
      <div :class="$style.detailsRow">
        <Input
          type="number"
          :class="[$style.quantity, { 'has-error': hasError('quantity') }]"
          :model-value="item.bulkUnits"
          @blur="updateQuantity($event.target.value)"
          @keyup.enter="$event.target.blur()"
          @keyup.delete="clearCartItemError(item)"
        />
        <div class="font-weight-medium text-small" :class="$style.numbers">
          <div class="text-small" :class="$style.price">
            <div v-if="item.discountType">
              <DiscountTag
                :campaign-label="campaignLabel"
                :classes="['mini-cart-item__discounted-icon']"
                :product="product"
              />
              <span v-if="showVolumeDiscountIndicator && !hasSaleDiscount" v-tooltip.top="item.volumeDiscount.name">
                <Icon data-test="icon|miniCartVolumeDiscountTag" name="tag" class="ll-mr-1 color-teal" />
              </span>
              {{ money(item.price) }}/{{ product.displayPriceUnit }}
            </div>
            <span :class="{ 'text-strike-through color-ice font-weight-normal': item.discountType }">
              {{ money(item.basePrice) }}<span v-if="!item.discountType">/{{ product.displayPriceUnit }}</span>
            </span>
          </div>
          <div class="color-ice-900 text-small">
            {{ money(item.subtotal) }}
            <template v-if="item.tax.amount"> (+ {{ $t('common.tax') }}) </template>
          </div>
        </div>
      </div>
      <div v-if="displayedValidationErrors.length" class="color-red text-small" :class="$style.detailsRow">
        {{ displayedValidationErrors.join(' ') }}
      </div>
    </div>
  </li>
</template>

<style lang="scss" module>
  .cartItem {
    &:not(:first-child) {
      border-top: border('thin', var(--color-ice));
    }

    .remove {
      background: transparent;
      border: none;
      height: 20px;
      margin-left: space(1);
      padding: 0;
      text-align: right;
      width: 20px;
    }
  }

  .image {
    height: 80px;
    min-width: 80px;
    object-fit: contain;
    width: 80px;
  }

  .name {
    @include clamp(2);

    height: 40px;
    line-height: typo('body', 'line-height');
    overflow: hidden;

    &:hover {
      text-decoration: none;
    }
  }

  .detailsRow {
    display: flex;

    &:not(:last-child) {
      margin-bottom: 4px;
    }
  }

  .quantity {
    flex: none;
    margin-right: space(3);
    width: 85px;

    input[type='number'] {
      appearance: textfield;

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        appearance: none;
        margin: 0;
      }
    }
  }

  .numbers {
    flex: 1 100%;
    text-align: right;
  }

  .price {
    margin-bottom: 4px;
  }
</style>
